import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, alpha } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CategoryIcon from '@mui/icons-material/Category';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import DashboardCard from '../components/StatisticsDashboard/DashboardCard';
import MonthRangeFilter from '../components/StatisticsDashboard/MonthRangeFilter';
import TrendAreaChart from '../components/StatisticsDashboard/TrendAreaChart';
import StatusBreakdownChart from '../components/StatisticsDashboard/StatusBreakdownChart';
import CategoryPieChart from '../components/StatisticsDashboard/CategoryPieChart';
import CategoryProgressList from '../components/StatisticsDashboard/CategoryProgressList';
import { getDefaultMonthRange, monthInputToRange, CATEGORICAL_COLORS, STATUS_COLORS } from '../components/StatisticsDashboard/chartUtils';

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || '').trim();

const DASHBOARD_THEME = {
    trend: { color: CATEGORICAL_COLORS[4], icon: TrendingUpIcon },
    status: { color: STATUS_COLORS.good, icon: FactCheckIcon },
    families: { color: CATEGORICAL_COLORS[1], icon: CategoryIcon },
    customers: { color: CATEGORICAL_COLORS[6], icon: StorefrontIcon },
};

const STATUS_GROUPS = [
    { key: 'created', label: 'נוצרו', color: CATEGORICAL_COLORS[0], icon: AddCircleOutlineIcon },
    { key: 'approvalRequired', label: 'ממתינות לאישור', color: STATUS_COLORS.warning, icon: HourglassTopIcon },
    { key: 'approved', label: 'אושרו', color: STATUS_COLORS.good, icon: CheckCircleIcon },
    { key: 'rejected', label: 'נדחו', color: STATUS_COLORS.critical, icon: CancelIcon },
];

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const SalesPromotionsStatisticsPage = () => {
    const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
    const [toMonth, setToMonth] = useState(DEFAULT_TO);

    const [monthlyStats, setMonthlyStats] = useState([]);
    const [familyStats, setFamilyStats] = useState([]);
    const [customerStats, setCustomerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });

    const loadStats = useCallback(async () => {
        if (!fromMonth || !toMonth) return;
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
            const params = new URLSearchParams({ startDate, endDate }).toString();
            const [monthlyRes, familyRes, customerRes] = await Promise.all([
                fetchRead('GET', `${BASE_URL}/api/salesPromotions/stats/monthly-stats?${params}`),
                fetchRead('GET', `${BASE_URL}/api/salesPromotions/stats/family-breakdown?${params}`),
                fetchRead('GET', `${BASE_URL}/api/salesPromotions/stats/customer-breakdown?${params}`),
            ]);
            setMonthlyStats(monthlyRes.monthlyStats || []);
            setFamilyStats(familyRes.familyStats || []);
            setCustomerStats(customerRes.customerStats || []);
        } catch (err) {
            console.error('Error loading sales-promotions statistics:', err);
            setError(err.message || 'שגיאה בטעינת הסטטיסטיקות');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth]);

    useEffect(() => {
        loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth]);

    const pageColor = CATEGORICAL_COLORS[4];

    return (
        <Box display="flex" flexDirection="column" gap={3} p={2}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(pageColor, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <BarChartIcon sx={{ color: pageColor, fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight={700}>סטטיסטיקות הזמנות קד"מ ודוגמיות</Typography>
                    <Typography variant="body2" color="text.secondary">
                        תמונת מצב לפי טווח התאריכים שנבחר — משתנה בכל הדשבורדים למטה
                    </Typography>
                </Box>
            </Box>

            <MonthRangeFilter
                fromMonth={fromMonth}
                toMonth={toMonth}
                onFromMonthChange={setFromMonth}
                onToMonthChange={setToMonth}
                color={pageColor}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} p={6}>
                    <CircularProgress sx={{ color: pageColor }} />
                    <Typography variant="body2" color="text.secondary">טוען נתונים...</Typography>
                </Box>
            ) : (
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="מגמת הזמנות חודשית"
                            subtitle="כמות הזמנות קד״מ ודוגמיות"
                            icon={DASHBOARD_THEME.trend.icon}
                            color={DASHBOARD_THEME.trend.color}
                        >
                            {(expanded) => (
                                <TrendAreaChart
                                    monthlyData={monthlyStats}
                                    expanded={expanded}
                                    color={DASHBOARD_THEME.trend.color}
                                    totalLabel="סה״כ הזמנות בטווח שנבחר"
                                    unitLabel="הזמנות"
                                    gradientId="salesPromotionsTrendGradient"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="פילוח לפי סטטוס הזמנה"
                            subtitle="מגמה חודשית — נוצרו / ממתינות / אושרו / נדחו"
                            icon={DASHBOARD_THEME.status.icon}
                            color={DASHBOARD_THEME.status.color}
                        >
                            {(expanded) => (
                                <StatusBreakdownChart
                                    monthlyData={monthlyStats}
                                    expanded={expanded}
                                    statusGroups={STATUS_GROUPS}
                                    totalLabel="סה״כ הזמנות"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="משפחות מוצר מובילות"
                            subtitle="כמות שורות הזמנה לכל משפחת מוצר בטווח שנבחר"
                            icon={DASHBOARD_THEME.families.icon}
                            color={DASHBOARD_THEME.families.color}
                        >
                            {(expanded) => (
                                <CategoryPieChart data={familyStats} expanded={expanded} unitLabel="שורות" />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="לקוחות מובילים"
                            subtitle="7 הלקוחות המובילים לפי כמות הזמנות"
                            icon={DASHBOARD_THEME.customers.icon}
                            color={DASHBOARD_THEME.customers.color}
                        >
                            {() => (
                                <CategoryProgressList
                                    data={customerStats}
                                    unitLabel="הזמנות"
                                    secondaryMetricLabel="אושרו"
                                    topN={7}
                                />
                            )}
                        </DashboardCard>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default SalesPromotionsStatisticsPage;
